<template>
  <b-modal
    size="lg"
    hide-footer
    no-close-on-esc
    no-close-on-backdrop
    :visible="visible"
    @change="$emit('update:visible', $event)"
  >
    <div class="iq-card mb-0" style="min-width: 300px;">
      <div class="iq-card-header d-flex justify-content-between">
        <div class="iq-header-title">
          <h4 class="card-title">{{ headingText }}</h4>
        </div>
      </div>
      <div class="iq-card-body">
        <div class="row mb-3">
          <div
            v-for="field in fields"
            :key="field.key"
            role="group"
            class="form-group"
            :class="`col-md-${field.colMd || 6}`"
          >
            <label :for="field.key" class="d-block form-label text-left">
              {{ field.label }}:
            </label>
            <div class="bv-no-focus-ring">
              <div role="group" class="input-group">
                <b-form-select
                  v-if="field.type === 'select'"
                  :id="field.key"
                  class="form-control"
                  v-model="form[field.key]"
                  :options="optionsFor(field)"
                  :class="errors[field.key] ? 'is-invalid' : ''"
                />
                <b-form-textarea
                  v-else-if="field.type === 'textarea'"
                  :id="field.key"
                  class="form-control"
                  v-model="form[field.key]"
                  :placeholder="field.placeholder || field.label"
                  :class="errors[field.key] ? 'is-invalid' : ''"
                />
                <input
                  v-else
                  class="form-control"
                  :type="field.type === 'number' ? 'number' : 'text'"
                  :id="field.key"
                  :placeholder="field.placeholder || field.label"
                  v-model="form[field.key]"
                  :class="errors[field.key] ? 'is-invalid' : ''"
                >
                <b-form-invalid-feedback v-if="errors[field.key]" force-show>
                  {{ errors[field.key] }}
                </b-form-invalid-feedback>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group">
          <b-button
            @click="$emit('cancel')"
            class="mr-1"
            size="sm"
            :disabled="isSubmit"
            variant="outline-danger"
          >
            <div class="mx-2">Cancel</div>
          </b-button>
          <b-button
            type="submit"
            @click="onSubmit"
            :disabled="isSubmit"
            variant="primary"
            size="sm"
            class="ml-1"
          >
            <div class="mx-2">
              <i v-if="isSubmit" class="ri-loader-line"></i>
              Submit
            </div>
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { createMasterCrud } from '@/services/masterCrud';
import { buildMasterParams } from '@/util/master/masterQuery';

export default {
  name: 'MasterFormModal',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, required: true },
    fields: { type: Array, required: true },
    value: { type: Object, default: () => ({}) },
    errors: { type: Object, default: () => ({}) },
    isSubmit: { type: Boolean, default: false },
    pkField: { type: String, required: true },
  },
  data() {
    return {
      form: {},
      sourceOptions: {},
    };
  },
  computed: {
    isEdit() {
      return !!this.value[this.pkField];
    },
    headingText() {
      return this.isEdit ? `Edit ${this.title}` : `Add New ${this.title}`;
    },
  },
  watch: {
    visible(open) {
      if (!open) return;
      this.form = this.buildForm();
      this.loadSourceOptions();
    },
  },
  methods: {
    // Vue 2 gak reaktif ke key baru, jadi semua key diisi sekaligus di sini.
    buildForm() {
      const form = {};
      this.fields.forEach((field) => {
        const raw = this.value[field.key];
        if (raw === undefined || raw === null || raw === '') {
          form[field.key] = field.default !== undefined ? field.default : '';
          return;
        }
        form[field.key] = field.type === 'select' ? this.matchOption(field, raw) : raw;
      });
      return form;
    },
    // API balikin angka 1, opsi bisa string '1'. Cocokin longgar biar dropdown gak kosong.
    matchOption(field, raw) {
      const hit = this.optionsFor(field)
        .find((option) => String(option.value).toLowerCase() === String(raw).toLowerCase());
      return hit ? hit.value : raw;
    },
    optionsFor(field) {
      if (field.options) return field.options;
      return this.sourceOptions[field.key] || [];
    },
    async loadSourceOptions() {
      const pending = this.fields.filter(
        (field) => field.type === 'select' && field.source && !this.sourceOptions[field.key],
      );
      if (!pending.length) return;

      const loaded = { ...this.sourceOptions };
      await Promise.all(pending.map(async (field) => {
        const { master, keyField, displayField } = field.source;
        try {
          const body = await createMasterCrud(master).list(buildMasterParams({ page: 1, max: 1000 }));
          const rows = Array.isArray(body.data) ? body.data : [];
          loaded[field.key] = rows.map((row) => ({ value: row[keyField], text: row[displayField] }));
        } catch (e) {
          loaded[field.key] = [];
        }
      }));
      this.sourceOptions = loaded;

      // Opsinya baru nyampe, nilai edit dicocokin ulang.
      pending.forEach((field) => {
        const raw = this.value[field.key];
        if (raw === undefined || raw === null || raw === '') return;
        this.$set(this.form, field.key, this.matchOption(field, raw));
      });
    },
    onSubmit() {
      const payload = {};
      this.fields.forEach((field) => {
        if (field.key === this.pkField) return;
        let value = this.form[field.key];
        if (value === '' || value === null || value === undefined) return;
        if (field.type === 'number') value = Number(value);
        payload[field.key] = value;
      });
      this.$emit('submit', payload);
    },
  },
};
</script>
